/**
 * Every `data-tour` anchor a guided tour may target (CONTRACT §3, ANCHORS.md).
 *
 * ── The rules ───────────────────────────────────────────────────────────────
 * - An ID is `<portal>.<surface>.<element>` in kebab-case. Tours target an
 *   element ONLY through one of these IDs — never translated text, a generated
 *   class, `nth-child` or a record ID.
 * - The writer who owns the page puts the attribute on the element
 *   (`data-tour="tenant.catalogue.filters"`, or a shared component's `tourId`
 *   prop). A native element takes `data-tour` directly.
 * - Exactly ONE element per ID is visible in any responsive context. Where the
 *   desktop and the phone render different elements, both may carry the ID only
 *   because CSS hides one of them; the engine resolves the visible match.
 * - Secret-bearing screens (sign-in, MFA, recovery codes, activation, secure
 *   links, `/account/security`, `/platform/security`) carry no anchor.
 * - A new ID, or a moved element, is a request to W1 (`requests/`); this list
 *   stays equal to ANCHORS.md plus accepted requests.
 *
 * ── `tenant.nav.<slug>` ─────────────────────────────────────────────────────
 * One ID per tenant navigation item in `AppLayout.tsx` as at eb92e2b. The slug
 * is the item's `to` without its leading slash, with `/` read as `-`
 * (`/my/requests` → `my-requests`, `/admin/users` → `admin-users`). The same ID
 * goes on the sidebar link and on the phone's bottom-navigation link for that
 * destination; CSS shows one of them at a time. `/account/security` is left
 * out on purpose: MFA stays outside the tour engine (brief §8).
 */
export const ANCHOR_IDS = [
  // ── Tenant workspace ─────────────────────────────────────────────────────
  'tenant.admin.org-identity',
  'tenant.admin.roles-list',
  'tenant.admin.settings',
  'tenant.admin.users-list',
  'tenant.approval.acting-for',
  'tenant.approval.budget',
  'tenant.approval.choices',
  'tenant.approval.decide-phone',
  'tenant.approval.decision-panel',
  'tenant.approval.lines',
  'tenant.approval.record',
  // PUX coach C02: the decision as the server recorded it (DecisionResult).
  'tenant.approval.result',
  'tenant.approval.why',
  'tenant.approval.workflow',
  'tenant.approvals.inbox',
  'tenant.audit.list',
  'tenant.budgets.list',
  'tenant.cart-review.controls',
  'tenant.cart-review.route',
  'tenant.cart-review.submit',
  'tenant.cart.add-from-catalogue',
  'tenant.cart.budget',
  'tenant.cart.details',
  'tenant.cart.lines',
  'tenant.cart.review',
  'tenant.catalogue.add',
  'tenant.catalogue.filters',
  'tenant.catalogue.item-facts',
  // PUX coach C01 (pux-coach-to-pux-filter-1): the search box, and the first
  // orderable item's name link, tagged in CataloguePage by its owner.
  'tenant.catalogue.item-link',
  'tenant.catalogue.off-catalogue',
  'tenant.catalogue.results',
  'tenant.catalogue.search',
  'tenant.catalogue.why-not',
  'tenant.dashboard.decisions',
  'tenant.dashboard.figures',
  'tenant.dashboard.latest-requests',
  'tenant.dashboard.new-request',
  'tenant.dashboard.your-work',
  'tenant.delegations.create',
  'tenant.delegations.list',
  // PUX coach C01, on the item's own page (AddToRequest through
  // CatalogueItemPage): the quantity, the add command, and its confirmation.
  'tenant.item.add',
  'tenant.item.added',
  'tenant.item.order-panel',
  'tenant.item.quantity',
  'tenant.orders.filters',
  'tenant.orders.list',
  'tenant.po.actions',
  'tenant.po.actions-phone',
  'tenant.po.completion',
  'tenant.po.header',
  'tenant.po.invoice-record',
  'tenant.po.invoices',
  'tenant.po.lines',
  'tenant.po.receipts',
  'tenant.po.status-dimensions',
  'tenant.po.supplier-responses',
  'tenant.po.versions',
  'tenant.receive.close-short',
  'tenant.receive.commit',
  'tenant.receive.delivery-details',
  'tenant.receive.facts',
  'tenant.receive.history',
  'tenant.receive.lines',
  // PUX coach C05: the server's confirmation that the receipt was recorded.
  'tenant.receive.recorded',
  'tenant.receiving.queue',
  'tenant.report.export',
  'tenant.report.filters',
  'tenant.reports.list',
  'tenant.reports.rail',
  'tenant.request.amend',
  'tenant.request.approvers',
  'tenant.request.generate-po',
  'tenant.request.next-action',
  'tenant.request.reorder',
  'tenant.request.resubmit',
  'tenant.request.send-to-sourcing',
  'tenant.request.status',
  'tenant.request.tabs',
  'tenant.requests.filters',
  'tenant.requests.list',
  'tenant.rfq.comparison',
  'tenant.rfq.decision',
  'tenant.rfq.draft-summary',
  'tenant.rfq.header',
  'tenant.rfq.invite',
  'tenant.rfq.invited',
  'tenant.rfq.open',
  'tenant.rfqnew.create',
  'tenant.rfqnew.lines',
  'tenant.rfqnew.summary',
  'tenant.rules.list',
  'tenant.setup.history',
  'tenant.setup.import',
  'tenant.setup.readiness',
  'tenant.shell.bottom-nav',
  'tenant.shell.help',
  'tenant.shell.language',
  'tenant.shell.menu',
  'tenant.shell.more',
  'tenant.shell.nav',
  'tenant.sourcing.queue',
  // Tenant navigation: one per item in AppLayout's NAV_GROUPS (see above).
  'tenant.nav.dashboard',
  'tenant.nav.catalogue',
  'tenant.nav.cart',
  'tenant.nav.my-requests',
  'tenant.nav.approvals',
  'tenant.nav.purchase-orders',
  'tenant.nav.receiving',
  'tenant.nav.sourcing',
  'tenant.nav.onboarding',
  'tenant.nav.suppliers',
  'tenant.nav.agreements',
  'tenant.nav.reports',
  'tenant.nav.budgets',
  'tenant.nav.admin-approval-rules',
  'tenant.nav.organization-regions',
  'tenant.nav.organization-warehouses',
  'tenant.nav.organization-categories',
  'tenant.nav.admin-organization',
  'tenant.nav.admin-users',
  'tenant.nav.admin-roles',
  'tenant.nav.admin-delegations',
  'tenant.nav.admin-currencies',
  'tenant.nav.admin-tax-codes',
  'tenant.nav.admin-settings',
  'tenant.nav.admin-purchasing-units',
  'tenant.nav.admin-catalogue',
  'tenant.nav.admin-audit',
  // ── Supplier portal ──────────────────────────────────────────────────────
  'vendor.home.work',
  'vendor.order.acknowledge',
  'vendor.order.dispatch',
  // PUX coach C06: the server's confirmation of a dispatch, and of an answer.
  'vendor.order.dispatched',
  'vendor.order.document',
  'vendor.order.header',
  'vendor.order.invoice',
  'vendor.order.lines',
  'vendor.order.reack-notice',
  'vendor.order.reply-answers',
  'vendor.order.reply-date',
  'vendor.order.reply-send',
  'vendor.order.reply-sent',
  'vendor.order.status',
  'vendor.orders.awaiting',
  'vendor.orders.list',
  'vendor.rfq.attachments',
  'vendor.rfq.closed-notice',
  'vendor.rfq.current-offer',
  'vendor.rfq.header',
  'vendor.rfq.offer',
  'vendor.rfq.requirement',
  'vendor.rfq.send-bar',
  'vendor.rfq.send-card',
  'vendor.rfq.validity',
  'vendor.rfqs.list',
  'vendor.shell.context',
  'vendor.shell.help',
  'vendor.shell.identity',
  'vendor.shell.language',
  'vendor.shell.nav',
  // ── Operator console ─────────────────────────────────────────────────────
  'platform.create.commit',
  // PUX coach C08: the organisation created, with its invitation's delivery.
  'platform.create.provisioned',
  'platform.org.administrator',
  'platform.org.capacity',
  'platform.org.history',
  'platform.org.not-in-scope',
  'platform.org.resend',
  'platform.org.setup',
  'platform.org.tabs',
  'platform.orgs.create',
  'platform.orgs.filters',
  'platform.orgs.list',
  'platform.overview.definitions',
  'platform.overview.metrics',
  'platform.shell.help',
  'platform.shell.nav',
  // Below 1024px the console's navigation is behind its "Platform operations" button.
  'platform.shell.nav-phone',
] as const;

export type AnchorId = (typeof ANCHOR_IDS)[number];

const KNOWN: ReadonlySet<string> = new Set(ANCHOR_IDS);

/** Whether a string is a registered anchor ID (for untyped input, such as stored state). */
export function isAnchorId(value: unknown): value is AnchorId {
  return typeof value === 'string' && KNOWN.has(value);
}

/**
 * State probes (WC-04, UI-6): the anchors that also say what state the server
 * reports for the thing they mark, as `data-guidance-state="<value>"` — a
 * shared component's `tourState` prop, or the attribute on a native element.
 *
 * ── The rules ───────────────────────────────────────────────────────────────
 * - Only a registered anchor carries one, and only with a value listed here.
 * - The value is rendered from SERVER data only — a status the API returned,
 *   whether a server list has items, whether the server accepted the add —
 *   never from what the person typed, and never a record ID, a name or an
 *   amount (a unit test holds every value to a short lowercase token).
 * - A coach reads it (`resolveState`) to confirm what the person's action did,
 *   and to resume after a reload, when the bus event is gone. A value not
 *   listed here reads as no state at all.
 */
const REQUEST_STATUSES = [
  'draft',
  'pending_approval',
  'approved',
  'returned',
  'rejected',
  'cancelled',
  'procurement_review',
  'sourcing_required',
  'procurement_processing',
  'po_created',
] as const;

export const ANCHOR_STATES: Partial<Record<AnchorId, readonly string[]>> = {
  // C03/C04: authoritative detail fields, never combined business dimensions.
  'tenant.rfq.header': ['draft', 'open', 'awarded', 'no_award', 'cancelled'],
  'tenant.po.status-dimensions': ['draft', 'issued', 'sent', 'active', 'completed', 'cancelled'],
  'tenant.po.header': ['sent', 'not_sent'],
  // The catalogue's results: a server page with items, or the empty state.
  'tenant.catalogue.results': ['empty', 'has-items'],
  // The item page's add command was accepted by the server (its response is on screen).
  'tenant.item.added': ['added'],
  // The basket's lines, as `GET /cart` returned them.
  'tenant.cart.lines': ['has-items'],
  // The review's commit, as the server's validation decided (`can_submit`).
  'tenant.cart-review.submit': ['ready', 'blocked'],
  // The request's status, as `GET /requests/:id` returned it (`RequestStatus`).
  'tenant.request.status': REQUEST_STATUSES,
  // C02 — the approvals inbox: the server's list with items, or its empty state.
  'tenant.approvals.inbox': ['empty', 'has-items'],
  // C02 — after a decision: the request's status in the decision's response.
  'tenant.approval.result': REQUEST_STATUSES,
  // C05 — the receipt the server accepted (its response is on screen).
  'tenant.receive.recorded': ['recorded'],
  // C05 — the order's fulfilment, as `GET /purchase-orders/:id` returned it.
  'tenant.receive.facts': [
    'not_dispatched',
    'dispatched',
    'partially_received',
    'received',
    'closed_short',
  ],
  // C07 — the supplier's enquiries: the server's list, or its empty state.
  'vendor.rfqs.list': ['empty', 'has-items'],
  // C07 — the supplier's invitation status, as the enquiry's API returned it.
  'vendor.rfq.offer': ['invited', 'responded', 'late', 'incomplete', 'withdrawn', 'non_responsive'],
  // C06 — the order's supplier-response status (`VendorResponseStatus`).
  'vendor.order.status': ['pending', 'acknowledged', 'query', 'partially_available', 'declined'],
  // C06 — the server accepted the answer / the dispatch (their responses are on screen).
  'vendor.order.reply-sent': ['sent'],
  'vendor.order.dispatched': ['dispatched'],
  // C08 — the organisation the server created: its invitation's delivery.
  'platform.create.provisioned': ['handed_to_mail_transport', 'not_sent'],
};

/** A probe value, if it is one this anchor may carry; anything else is no state. */
export function allowedState(anchor: AnchorId, value: string | null): string | null {
  return value !== null && ANCHOR_STATES[anchor]?.includes(value) === true ? value : null;
}
